import { useState, useEffect, useCallback, useRef } from "react"
import { useApi } from "@/lib/api"
import { useAuth } from "@/contexts/auth-context"
import type { SharedReportListItem, GridState } from "@/types/report"

interface SaveViewParams {
  stateName: string
  stateDescription: string
  state: GridState
  isPublic?: boolean
  userProfileIds?: number[]
  teamGuids?: string[]
  existingStateId?: number
}

export function useViewState(componentName: string) {
  const { reportsApi } = useApi()
  const { isAuthenticated } = useAuth()
  const [views, setViews] = useState<SharedReportListItem[]>([])
  const [activeViewId, setActiveViewId] = useState<number | null>(null)
  const [isLoadingViews, setIsLoadingViews] = useState(false)
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Load all saved views for this report
  const loadViews = useCallback(async () => {
    if (!isAuthenticated) return
    setIsLoadingViews(true)
    try {
      const result = await reportsApi.get<SharedReportListItem[]>(
        `/Report/reportSharing/searchStateList/componentName/${componentName}`
      )
      setViews(result ?? [])
    } catch (err) {
      console.error("Failed to load views:", err)
    } finally {
      setIsLoadingViews(false)
    }
  }, [componentName, isAuthenticated, reportsApi])

  // Load a specific view's full state
  const loadViewState = useCallback(async (stateId: number): Promise<GridState | null> => {
    try {
      const result = await reportsApi.get<{ state: string; State?: string }>(
        `/Report/reportSharing/userProfileSearchState/${stateId}`
      )
      const stateJson = result?.state ?? result?.State
      if (stateJson) {
        return JSON.parse(stateJson)
      }
    } catch (err) {
      console.error("Failed to load view state:", err)
    }
    return null
  }, [reportsApi])

  // Save or update a named view
  const saveView = useCallback(async (params: SaveViewParams): Promise<number | null> => {
    try {
      const result = await reportsApi.post<{ userProfileSearchStateId: number }>(
        `/Report/reportSharing/userProfileSearchStateCompleteSave`,
        {
          UserProfileSearchStateId: params.existingStateId ?? 0,
          StateName: params.stateName,
          StateDescription: params.stateDescription,
          ComponentName: componentName,
          State: JSON.stringify(params.state),
          IsPublic: params.isPublic ?? false,
          UserProfileIds: params.userProfileIds ?? [],
          TeamGuids: params.teamGuids ?? [],
        }
      )
      await loadViews()
      return result?.userProfileSearchStateId ?? null
    } catch (err) {
      console.error("Failed to save view:", err)
      return null
    }
  }, [componentName, loadViews, reportsApi])

  // Delete a saved view
  const deleteView = useCallback(async (stateId: number) => {
    try {
      await reportsApi.delete(`/Report/reportSharing/userProfileSearchStateDelete/${stateId}`)
      setViews((prev) => prev.filter((v) => v.userProfileSearchStateId !== stateId))
      if (activeViewId === stateId) setActiveViewId(null)
    } catch (err) {
      console.error("Failed to delete view:", err)
    }
  }, [activeViewId, reportsApi])

  // Auto-save session state (unnamed, debounced)
  const autoSaveSessionState = useCallback((state: GridState) => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
    autoSaveTimer.current = setTimeout(async () => {
      try {
        await reportsApi.post(`/Report/reportViewState/${componentName}`, {
          State: JSON.stringify(state),
          SelectedReportId: activeViewId ?? 0,
          ViewDropdownState: "",
        })
      } catch (err) {
        console.error("Failed to auto-save session state:", err)
      }
    }, 1000)
  }, [componentName, activeViewId, reportsApi])

  // Restore last session state
  const restoreSessionState = useCallback(async (): Promise<GridState | null> => {
    try {
      const result = await reportsApi.get<{ State: string; SelectedReportId: number }>(
        `/Report/reportViewState/${componentName}`
      )
      if (result?.State) {
        if (result.SelectedReportId) setActiveViewId(result.SelectedReportId)
        return JSON.parse(result.State)
      }
    } catch (err) {
      console.error("Failed to restore session state:", err)
    }
    return null
  }, [componentName, reportsApi])

  // Load views on mount
  useEffect(() => {
    loadViews()
  }, [loadViews])

  // Cleanup auto-save timer
  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
    }
  }, [])

  return {
    views,
    activeViewId,
    setActiveViewId,
    isLoadingViews,
    loadViews,
    loadViewState,
    saveView,
    deleteView,
    autoSaveSessionState,
    restoreSessionState,
  }
}
